import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CssBaseline,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import { Helmet } from 'react-helmet-async';
import { publicApi, PublicReceipt } from '../../services/publicApi';

// Elevair brand colors
const COLORS = {
  primary: '#E07A5F',
  secondary: '#1D3557',
  background: '#FDF8F3',
  success: '#38A169',
  text: '#2D3436',
  textSecondary: '#636E72',
};

export const PaymentSuccessPage = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();

  const { data: receipt, isLoading, error } = useQuery<PublicReceipt>({
    queryKey: ['publicReceipt', invoiceId],
    queryFn: () => publicApi.getReceipt(invoiceId!),
    enabled: !!invoiceId,
    retry: false,
  });

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownloadReceipt = () => {
    if (invoiceId) {
      window.open(publicApi.getReceiptPdfUrl(invoiceId), '_blank');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: COLORS.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ color: COLORS.primary }} />
        </Box>
      </>
    );
  }

  // Error state
  if (error || !receipt) {
    const errorMessage = error instanceof Error ? error.message : 'Invoice not found or payment has not been completed.';

    return (
      <>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            backgroundColor: COLORS.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              maxWidth: 500,
              width: '100%',
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #E0E0E0',
            }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              If you believe this is an error, please contact the business directly.
            </Typography>
          </Paper>
        </Box>
      </>
    );
  }

  // Success state
  return (
    <>
      <Helmet>
        <title>Payment Successful | Receipt #{receipt.invoiceNumber}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <CssBaseline />

      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: COLORS.background,
          py: { xs: 3, md: 6 },
          px: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          {/* Success Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: COLORS.success,
                mb: 2,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: COLORS.secondary,
                mb: 1,
              }}
            >
              Payment Successful
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: COLORS.textSecondary }}
            >
              Thank you for your payment!
            </Typography>
          </Box>

          {/* Receipt Card */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              border: '1px solid #E0E0E0',
              mb: 3,
            }}
          >
            {/* Receipt Header */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: COLORS.secondary }}
              >
                {receipt.business.name}
              </Typography>
              {receipt.business.email && (
                <Typography variant="body2" color="text.secondary">
                  {receipt.business.email}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Receipt Details */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="overline"
                sx={{ color: COLORS.textSecondary, letterSpacing: 1 }}
              >
                Receipt Details
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Receipt Number
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0, pr: 0, py: 0.5, textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {receipt.invoiceNumber}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Date Paid
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0, pr: 0, py: 0.5, textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {receipt.paidAt ? formatDate(receipt.paidAt) : 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Billed To
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0, pr: 0, py: 0.5, textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {receipt.client.name}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Line Items */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="overline"
                sx={{ color: COLORS.textSecondary, letterSpacing: 1 }}
              >
                Items
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableBody>
                  {receipt.lineItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ border: 0, pl: 0, py: 1 }}>
                        <Typography variant="body2">
                          {item.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.quantity} x {formatCurrency(item.unitPrice, receipt.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ border: 0, pr: 0, py: 1, textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(item.amount, receipt.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Totals */}
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: 0, pr: 0, py: 0.5, textAlign: 'right' }}>
                    <Typography variant="body2">
                      {formatCurrency(receipt.subtotal, receipt.currency)}
                    </Typography>
                  </TableCell>
                </TableRow>
                {receipt.discount && receipt.discount > 0 && (
                  <TableRow>
                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Discount
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0, pr: 0, py: 0.5, textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ color: COLORS.success }}>
                        -{formatCurrency(receipt.discount, receipt.currency)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {receipt.tax && receipt.tax > 0 && (
                  <TableRow>
                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Tax{receipt.taxRate ? ` (${(receipt.taxRate * 100).toFixed(1)}%)` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0, pr: 0, py: 0.5, textAlign: 'right' }}>
                      <Typography variant="body2">
                        {formatCurrency(receipt.tax, receipt.currency)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell sx={{ border: 0, pl: 0, pt: 2, pb: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Total Paid
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ border: 0, pr: 0, pt: 2, pb: 0, textAlign: 'right' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.secondary }}>
                      {formatCurrency(receipt.total, receipt.currency)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>

          {/* Download Button */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReceipt}
              sx={{
                backgroundColor: COLORS.primary,
                '&:hover': {
                  backgroundColor: '#C66549',
                },
                px: 4,
                py: 1.5,
                borderRadius: 2,
              }}
            >
              Download Receipt
            </Button>
          </Box>

          {/* Contact Info */}
          {receipt.business.email && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Questions about your payment? Contact{' '}
                <a
                  href={`mailto:${receipt.business.email}`}
                  style={{ color: COLORS.primary, textDecoration: 'none' }}
                >
                  {receipt.business.email}
                </a>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default PaymentSuccessPage;
